import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { MtaBlockedReason } from '@acorn-portal/shared/src/pages/policy/types';
import { MtaIneligibilityReason } from '@acorn-portal/shared/src/services/api';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';
import { Alert } from '@mui/material';
import ContactDetailsContent from './notification-content/contact-details-content';
import EnvDisabledContent from './notification-content/env-disabled-content';

interface IMtaAccessNotification {
	reasonBlocked: MtaBlockedReason;
}

/**
 * Helper component to manage messages that show when a user is blocked from accessing an MTA journey.
 * @param reasonBlocked - the reason why MTA access is blocked, used to determine which Alert to show
 * @remarks - At present, we only deal with 2 scenarios - MTAs being switched off at an environment level and a fallback of displaying contact details.
 *  - The contact number to display in the notification is worked out by processing the state value for `fullPolicy.product` with `getPhoneNumber`.
 */
const MtaAccessNotification = ({ reasonBlocked }: IMtaAccessNotification) => {
	const product = useAppSelector((state) => state.fullPolicy.product);
	const contactNumber = getPhoneNumber(product, 'CONTACT');

	// Return null for None reason
	if (reasonBlocked === MtaIneligibilityReason.None || !reasonBlocked) return null;

	// Handle specific blocked reasons
	const renderContent = () => {
		switch (reasonBlocked) {
			case 'EnvDisabled':
				return <EnvDisabledContent />;
			// Add other specific reasons here as needed
			default:
				// Fallback for all other blocked reasons
				return <ContactDetailsContent contactNumber={contactNumber} />;
		}
	};

	return (
		<Alert variant='outlined' severity='info' sx={{ fontSize: '14px', a: { color: 'inherit' } }}>
			{renderContent()}
		</Alert>
	);
};

export default MtaAccessNotification;
